// Chat engine for the "Chat with me" widget.
//
// Primary path: every question goes to a Cloudflare Worker
// (REACT_APP_CHAT_WORKER_URL) that reads Ehsan's full knowledge base and answers
// the specific question with a capable model on Workers AI's free tier. The
// answer is streamed and always labeled AI-generated.
//
// Safety net: if the Worker isn't configured or can't be reached, a tiny
// keyword matcher (no model, no download) returns Ehsan's closest curated
// answer, or a "contact Ehsan directly" note. This keeps the widget useful
// offline of the Worker.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  KNOWLEDGE_BASE,
  KB_BY_ID,
  NO_MATCH_MESSAGE,
  FALLBACK_UNAVAILABLE_MESSAGE,
} from './knowledgeBase';

const WORKER_URL = (process.env.REACT_APP_CHAT_WORKER_URL || '').trim();
const REQUEST_TIMEOUT_MS = 25000;

// ---------------------------------------------------------------------------
// Keyword safety-net matcher
// ---------------------------------------------------------------------------

const STOP_WORDS = new Set(
  'a an and or the is are am was were be been being do does did to of in on at for with about your you i me my he she it they them his her that this these those as by from into over under again more most not no yes any some ehsan faghih what when where why how which who whom can could would should tell give show list about'.split(
    ' '
  )
);

// Very light stemmer so "teams" ↔ "team", "movies" ↔ "movie", "working" ↔ "work".
function stem(word) {
  return word
    .replace(/(ies)$/, 'y')
    .replace(/(sses|ches|shes|xes)$/, (m) => m.slice(0, -2))
    .replace(/([^s])s$/, '$1')
    .replace(/(ing|ed)$/, '');
}

function tokenize(text) {
  return (text.toLowerCase().match(/[a-z0-9]+/g) || [])
    .filter((t) => t.length > 2 && !STOP_WORDS.has(t))
    .map(stem)
    .filter((t) => t.length > 2);
}

const KEYWORD_INDEX = KNOWLEDGE_BASE.map((entry) => ({
  id: entry.id,
  // Weight the question/aliases more than the answer body.
  tokens: new Set(tokenize([entry.question, ...(entry.aliases || [])].join(' '))),
  answerTokens: new Set(tokenize(entry.answer)),
}));

function keywordMatch(query) {
  const queryTokens = [...new Set(tokenize(query))];
  if (!queryTokens.length) return null;
  let best = null;
  for (const item of KEYWORD_INDEX) {
    let hits = 0;
    for (const token of queryTokens) {
      if (item.tokens.has(token)) hits += 1;
      else if (item.answerTokens.has(token)) hits += 0.5;
    }
    const score = hits / queryTokens.length;
    if (!best || score > best.score) best = { id: item.id, score };
  }
  return best && best.score >= 0.34 ? best : null;
}

function safetyNetAnswer(question) {
  const match = keywordMatch(question);
  const entry = match && KB_BY_ID[match.id];
  return entry
    ? { text: entry.answer, source: 'prepared' }
    : { text: FALLBACK_UNAVAILABLE_MESSAGE, source: 'system' };
}

// ---------------------------------------------------------------------------
// Worker stream
// ---------------------------------------------------------------------------

// Yields { token } / { done } / throws. Caller handles the safety net.
async function* streamWorker(question, history, signal) {
  const response = await fetch(WORKER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      question,
      history: history.slice(-6).map((m) => ({ role: m.role, content: m.text })),
    }),
    signal,
  });

  if (!response.ok || !response.body) {
    throw new Error(`Worker responded ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) continue;
      const payload = trimmed.slice(5).trim();
      if (payload === '[DONE]') {
        yield { done: true };
        return;
      }
      try {
        const parsed = JSON.parse(payload);
        if (parsed.error) throw new Error(parsed.error);
        if (parsed.response) yield { token: parsed.response };
      } catch (err) {
        if (err.message && !err.message.startsWith('Unexpected')) throw err;
      }
    }
  }
  yield { done: true };
}

async function runWorker(question, history, signal, onToken) {
  let streamed = '';
  for await (const chunk of streamWorker(question, history, signal)) {
    if (chunk.token) {
      streamed += chunk.token;
      onToken(streamed);
    }
    if (chunk.done) break;
  }
  return streamed.trim();
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useChatBot() {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | thinking | streaming
  const abortRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setStatus('idle');
  }, []);

  const send = useCallback(
    async (rawText) => {
      const text = rawText.trim().slice(0, 500);
      if (!text || status === 'thinking' || status === 'streaming') return;

      const controller = new AbortController();
      abortRef.current = controller;
      const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      const historySnapshot = [...messages, { role: 'user', text }];
      setMessages((prev) => [...prev, { role: 'user', text }]);

      const replaceLast = (msg) =>
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = msg;
          return next;
        });

      // No Worker configured -> keyword safety net only.
      if (!WORKER_URL) {
        setStatus('thinking');
        const fallback = safetyNetAnswer(text);
        setMessages((prev) => [...prev, { role: 'assistant', ...fallback }]);
        setStatus('idle');
        clearTimeout(timeout);
        return;
      }

      setStatus('streaming');
      setMessages((prev) => [...prev, { role: 'assistant', text: '', source: 'ai', pending: true }]);

      try {
        const answer = await runWorker(text, historySnapshot, controller.signal, (soFar) =>
          replaceLast({ role: 'assistant', text: soFar, source: 'ai', pending: true })
        );
        replaceLast(
          answer
            ? { role: 'assistant', text: answer, source: 'ai' }
            : { role: 'assistant', text: NO_MATCH_MESSAGE, source: 'system' }
        );
      } catch (err) {
        if (controller.signal.aborted && !err?.name?.includes('Abort')) {
          // real abort from unmount/reset — leave it
        }
        // Worker unreachable / errored -> keyword safety net.
        const fallback = safetyNetAnswer(text);
        replaceLast({ role: 'assistant', ...fallback });
      } finally {
        clearTimeout(timeout);
        setStatus('idle');
      }
    },
    [messages, status]
  );

  return { messages, status, send, reset, fallbackEnabled: Boolean(WORKER_URL) };
}
